import React from 'react';
import { View, TouchableOpacity, StyleSheet, Animated } from 'react-native';
import { Text } from '@/components/common/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useReprocessFundingsMutation } from '@/store/api';
import { IFundingTableRow, BulkReprocessHandler } from '@/types/admin/funding';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useSpinAnimation } from '@/hooks/common/useSpinAnimation';

interface FundingMassActionsProps {
  selectedRows: IFundingTableRow[];
  onClose: () => void;
  onReprocessComplete: BulkReprocessHandler;
  onDownload?: () => void;
}

export const FundingMassActions: React.FC<FundingMassActionsProps> = ({
  selectedRows,
  onClose,
  onReprocessComplete,
  onDownload,
}) => {
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { admin } = useAdminColors();
  const { showSuccess, showError } = useToast();
  const adminUser = useAdminUser();

  const [reprocessFundings, { isLoading: isReprocessLoading }] = useReprocessFundingsMutation();
  const spin = useSpinAnimation(isReprocessLoading);

  const handleBulkReprocess = async () => {
    if (!adminUser.selectedOrganizationId) {
      showError('No organization selected');
      return;
    }

    try {
      const fundingIds = selectedRows.map((row) => row.id);

      const response = await reprocessFundings({
        ids: fundingIds,
        organizationId: adminUser.selectedOrganizationId,
      }).unwrap();

      if (response && response.response) {
        onReprocessComplete(response.response);
      }

      showSuccess('Reprocessing completed successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to reprocess fundings:', error);
      showError('Failed to reprocess fundings. Please try again.');
    }
  };

  return (
    <Animated.View style={[styles.container, { bottom: insets.bottom + 20 }]}>
      <View style={styles.panel}>
        <View style={styles.content}>
          {/* Selection Count */}
          <View style={styles.selectionInfo}>
            <Text style={styles.selectionText}>{selectedRows.length} selected</Text>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionsContainer}>
            {/* Reprocess Button */}
            <TouchableOpacity
              style={[styles.actionButton, styles.reprocessButton]}
              onPress={handleBulkReprocess}
              disabled={isReprocessLoading}
            >
              <View style={styles.buttonContent}>
                <Animated.View style={{ transform: [{ rotate: spin }] }}>
                  <SVG_COMPONENTS.REFRESH width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                </Animated.View>
                <Text style={styles.reprocessButtonText}>Reprocess</Text>
              </View>
            </TouchableOpacity>

            {/* Download Button (only if provided) */}
            {onDownload && (
              <TouchableOpacity
                style={[styles.actionButton, styles.downloadButton]}
                onPress={onDownload}
              >
                <View style={styles.buttonContent}>
                  <SVG_COMPONENTS.DOWNLOAD width={16} height={16} color="#FFFFFF" />
                  <Text style={styles.downloadButtonText}>Download</Text>
                </View>
              </TouchableOpacity>
            )}

            {/* Close Button */}
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <SVG_COMPONENTS.CANCEL_X width={16} height={16} color={admin.dataCell.textMuted} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      container: {
        position: 'absolute',
        left: 20,
        right: 20,
        zIndex: 1000,
      },
      panel: {
        backgroundColor: colors.bg.modal,
        borderRadius: 12,
        borderTopWidth: 3,
        borderTopColor: PRIMITIVE_COLORS.resioBlue,
        borderWidth: 1,
        borderColor: components.admin.dataTable.border,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
      content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
      },
      selectionInfo: {
        flex: 1,
      },
      selectionText: {
        color: components.admin.dataCell.text,
        fontSize: 16,
        fontWeight: '600',
      },
      actionsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
      },
      actionButton: {
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 8,
        minWidth: 100,
      },
      reprocessButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: PRIMITIVE_COLORS.resioBlue,
      },
      downloadButton: {
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
        borderWidth: 0,
      },
      buttonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      },
      reprocessButtonText: {
        color: PRIMITIVE_COLORS.resioBlue,
        fontSize: 14,
        fontWeight: '600',
      },
      downloadButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
      },
      closeButton: {
        padding: 8,
      },
    }),
  );
